import { useEffect, useRef, useState, useCallback } from "react";
import {
  collection,
  doc,
  query,
  orderBy,
  onSnapshot,
  limit,
  setDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../data/firebase";

/**
 * Unread badges for the chat.
 *
 * Read position is stored per user in Firestore at
 * tutors/{uid}/chatState/{conversationKey} with a `lastReadAt` timestamp, the
 * same documents the mobile app uses, so reading a thread on one device clears
 * it everywhere. Keys are "channel:<id>", "dm:<dmId>" and "senior:<threadOwnerUid>".
 *
 * A conversation with no read record is treated as read up to page load, so a
 * new senior tutor doesn't inherit a wall of unread badges but still sees new
 * messages arrive. (Mobile treats "no record" as zero unread.)
 */

const getDmId = (uid1, uid2) => [uid1, uid2].sort().join("_");
const SENIOR_TUTOR_DM_COLLECTION = "seniorTutorDMs";
const MESSAGE_WINDOW = 50; // how many recent messages we look at per conversation

export const conversationKey = (kind, id) => `${kind}:${id}`;

const useUnreadCounts = ({
  currentUserUid,
  accessibleChannels,
  existingDMs,
  activeChannel,
  activeDM,
  activeSeniorTutorDM,
  isSeniorTutor,
  seniorTutorInboxSenders,
  onToast,
}) => {
  const [channelUnread, setChannelUnread] = useState({});
  const [dmUnread, setDmUnread] = useState({});
  const [seniorDmUnread, setSeniorDmUnread] = useState({});

  // lastReadAt (ms) per conversation key, from Firestore.
  const [readState, setReadState] = useState(null); // null until first snapshot
  const readStateRef = useRef({});
  // Latest message docs per conversation key, so counts can be recomputed when read state changes.
  const messagesRef = useRef({});
  const firstSnapshotRef = useRef({});

  // Always-current view of which conversation is open, readable inside listeners.
  const activeRef = useRef({});
  activeRef.current = { activeChannel, activeDM, activeSeniorTutorDM };

  const isActiveKey = useCallback(
    (key) => {
      const { activeChannel: ch, activeDM: dm, activeSeniorTutorDM: st } = activeRef.current;
      if (!currentUserUid) return false;
      if (st) return key === conversationKey("senior", st === "self" ? currentUserUid : st);
      if (dm) return key === conversationKey("dm", getDmId(currentUserUid, dm.uid));
      if (ch) return key === conversationKey("channel", ch);
      return false;
    },
    [currentUserUid]
  );

  const markRead = useCallback(
    (key, at = Timestamp.now()) => {
      if (!currentUserUid || !key) return;
      readStateRef.current[key] = at.toMillis();
      setDoc(
        doc(db, "tutors", currentUserUid, "chatState", key),
        { lastReadAt: at, updatedAt: serverTimestamp() },
        { merge: true }
      ).catch(() => {});
    },
    [currentUserUid]
  );

  // Conversations with no read record anywhere count as read up to the moment
  // this page loaded: old history stays quiet, anything new still gets a badge.
  const loadedAtRef = useRef(Date.now());

  const countUnread = useCallback(
    (key) => {
      const lastRead = readStateRef.current[key] || loadedAtRef.current;
      const msgs = messagesRef.current[key] || [];
      return msgs.filter((m) => m.senderId !== currentUserUid && (m.ts || 0) > lastRead).length;
    },
    [currentUserUid]
  );

  // Subscribe to the user's read state.
  useEffect(() => {
    if (!currentUserUid) return undefined;
    const unsub = onSnapshot(
      collection(db, "tutors", currentUserUid, "chatState"),
      (snap) => {
        const next = {};
        snap.docs.forEach((d) => {
          const t = d.data().lastReadAt;
          if (t?.toMillis) next[d.id] = t.toMillis();
        });
        // Keep any optimistic local marks that are newer than the server copy.
        Object.entries(readStateRef.current).forEach(([k, v]) => {
          if (!next[k] || v > next[k]) next[k] = v;
        });
        readStateRef.current = next;
        setReadState({ ...next });
      },
      () => setReadState({})
    );
    return () => unsub();
  }, [currentUserUid]);

  // When the open conversation changes, mark it read and clear its badge.
  useEffect(() => {
    if (!currentUserUid) return;
    if (activeSeniorTutorDM) {
      const owner = activeSeniorTutorDM === "self" ? currentUserUid : activeSeniorTutorDM;
      markRead(conversationKey("senior", owner));
      setSeniorDmUnread((prev) => ({ ...prev, [activeSeniorTutorDM]: 0 }));
    } else if (activeDM) {
      markRead(conversationKey("dm", getDmId(currentUserUid, activeDM.uid)));
      setDmUnread((prev) => ({ ...prev, [activeDM.uid]: 0 }));
    } else if (activeChannel) {
      markRead(conversationKey("channel", activeChannel));
      setChannelUnread((prev) => ({ ...prev, [activeChannel]: 0 }));
    }
  }, [activeChannel, activeDM, activeSeniorTutorDM, currentUserUid, markRead]);

  // Generic listener factory shared by channels, DMs and senior tutor threads.
  const listen = useCallback(
    ({ key, ref, setUnread, badgeId, toastMeta }) => {
      if (firstSnapshotRef.current[key] === undefined) firstSnapshotRef.current[key] = true;
      const q = query(ref, orderBy("timestamp", "desc"), limit(MESSAGE_WINDOW));
      return onSnapshot(q, (snap) => {
        const isFirst = firstSnapshotRef.current[key];
        firstSnapshotRef.current[key] = false;

        messagesRef.current[key] = snap.docs.map((d) => {
          const data = d.data();
          return { senderId: data.senderId, ts: data.timestamp?.toMillis?.() || 0 };
        });

        if (isActiveKey(key)) {
          // Reading it right now: keep it marked read as messages arrive.
          const newest = messagesRef.current[key][0]?.ts || 0;
          if (newest > (readStateRef.current[key] || 0)) markRead(key);
          setUnread((prev) => ({ ...prev, [badgeId]: 0 }));
          return;
        }

        setUnread((prev) => ({ ...prev, [badgeId]: countUnread(key) }));

        if (!isFirst && onToast) {
          const lastRead = readStateRef.current[key] || loadedAtRef.current;
          snap.docChanges().forEach((change) => {
            if (change.type !== "added") return;
            const data = change.doc.data();
            if (data.senderId === currentUserUid) return;
            if ((data.timestamp?.toMillis?.() || 0) <= lastRead) return;
            onToast({ ...toastMeta, senderName: data.senderName, message: data.message });
          });
        }
      });
    },
    [currentUserUid, isActiveKey, markRead, countUnread, onToast]
  );

  // Channel listeners
  useEffect(() => {
    if (!currentUserUid || readState === null || accessibleChannels.length === 0) return undefined;
    const unsubs = accessibleChannels.map((channelId) =>
      listen({
        key: conversationKey("channel", channelId),
        ref: collection(db, "chatMessages", channelId, "messages"),
        setUnread: setChannelUnread,
        badgeId: channelId,
        toastMeta: { type: "channel", channelId },
      })
    );
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserUid, readState === null, accessibleChannels.join(",")]);

  // DM listeners
  useEffect(() => {
    if (!currentUserUid || readState === null || existingDMs.length === 0) return undefined;
    const unsubs = existingDMs.map((tutor) => {
      const dmId = getDmId(currentUserUid, tutor.uid);
      return listen({
        key: conversationKey("dm", dmId),
        ref: collection(db, "directMessages", dmId, "messages"),
        setUnread: setDmUnread,
        badgeId: tutor.uid,
        toastMeta: { type: "dm", tutorUid: tutor.uid },
      });
    });
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserUid, readState === null, existingDMs.map((d) => d.uid).join(",")]);

  // Senior tutor thread listeners (inbox for the ST on duty, own thread for everyone else)
  useEffect(() => {
    if (!currentUserUid || readState === null) return undefined;
    const senderIds = isSeniorTutor ? seniorTutorInboxSenders.map((s) => s.uid) : ["self"];
    if (senderIds.length === 0) return undefined;
    const unsubs = senderIds.map((senderId) => {
      const owner = senderId === "self" ? currentUserUid : senderId;
      return listen({
        key: conversationKey("senior", owner),
        ref: collection(db, SENIOR_TUTOR_DM_COLLECTION, owner, "messages"),
        setUnread: setSeniorDmUnread,
        badgeId: senderId,
        toastMeta: { type: "seniorDm", senderId },
      });
    });
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserUid, readState === null, isSeniorTutor, seniorTutorInboxSenders.map((s) => s.uid).join(",")]);

  // When read state changes (e.g. read on another device), recompute every badge.
  useEffect(() => {
    if (!currentUserUid || readState === null) return;
    const recompute = (setter, entries) =>
      setter((prev) => {
        const next = { ...prev };
        entries.forEach(([badgeId, key]) => {
          next[badgeId] = isActiveKey(key) ? 0 : countUnread(key);
        });
        return next;
      });
    recompute(
      setChannelUnread,
      accessibleChannels.map((id) => [id, conversationKey("channel", id)])
    );
    recompute(
      setDmUnread,
      existingDMs.map((t) => [t.uid, conversationKey("dm", getDmId(currentUserUid, t.uid))])
    );
    recompute(
      setSeniorDmUnread,
      (isSeniorTutor ? seniorTutorInboxSenders.map((s) => s.uid) : ["self"]).map((id) => [
        id,
        conversationKey("senior", id === "self" ? currentUserUid : id),
      ])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readState]);

  return { channelUnread, dmUnread, seniorDmUnread };
};

export default useUnreadCounts;
